<template>
  <div>
    <template v-if="headerData">
      <template v-if="cleanedHeader === 'standardDark' || cleanedHeader === 'standardWhite'">
        <UtilityNavbar
          :create-student-account-button="headerData.createStudentAccountButton"
          :signin-button="headerData.signinButton"
          :signup-button="headerData.signupButton"
          :customer-home="headerData.customerHome"
          :open-onshape="headerData.openOnshape"
          :ai-advisor="headerData.aiAdvisor"
        />
        <Header
          v-bind="headerData"
          :light="isHeaderLight"
          :class="{
            'standard-header': true,
          }"
        />
        <ClientOnly>
          <template v-if="isUnifiedSignupForm">
            <LpHeader
              :light="false"
              v-bind="headerData"
              :class="{ 'uss-lp-header': true }"
            />
            <HekateProgressBar />
            <HekateLoader />
          </template>
        </ClientOnly>

        <UssFormApp
          v-if="isUnifiedSignupForm"
          :id="ussIsStartScreen ? '' : ussFormContainerId"
          :class="{ 'uss-form-container mb-0': true }"
          :style="`--checkImageSrc: url(${checkImageSrc})`"
        />

        <main
          v-if="ussIsStartScreen"
          :id="ussFormContainer"
          ref="page"
          :class="{
            'uss-form-container mb-0': true,
            'standard-min-height': footerData && cleanedFooter === 'standard',
            'lp-min-height': footerData && cleanedFooter === 'lp',
          }"
          :style="`--checkImageSrc: url(${checkImageSrc})`"
        >
          <slot />
        </main>
      </template>

      <div v-if="cleanedHeader === 'lpDark' || cleanedHeader === 'lpWhite'">
        <div class="lp-header-wrapper">
          <div class="uss-lp-absolute-header absolute z-20 w-full">
            <LpHeader
              :light="isHeaderLight"
              v-bind="headerData"
            />
          </div>
          <ClientOnly>
            <template v-if="isUnifiedSignupForm">
              <LpHeader
                :light="false"
                v-bind="headerData"
                :class="{ 'uss-lp-header': true }"
              />
              <HekateProgressBar />
              <HekateLoader />
            </template>
          </ClientOnly>
          <UssFormApp
            v-if="isUnifiedSignupForm"
            :id="ussIsStartScreen ? '' : ussFormContainerId"
            :class="{ 'uss-form-container mb-0': true }"
            :style="`--checkImageSrc: url(${checkImageSrc})`"
          />

          <main
            v-if="ussIsStartScreen"
            :id="ussFormContainer"
            ref="page"
            :class="{
              'uss-form-container mb-0': true,
              'standard-min-height': footerData && cleanedFooter === 'standard',
              'lp-min-height': footerData && cleanedFooter === 'lp',
            }"
            :style="`--checkImageSrc: url(${checkImageSrc})`"
          >
            <slot />
          </main>
        </div>
      </div>
    </template>
    <template v-if="eduData">
      <template v-if="cleanedHeader === 'edu'">
        <div
          :class="{
            'education-header fixed top-0 z-40 w-full bg-www-color-white': true,
          }"
        >
          <EducationHeaderComponent
            v-if="eduData"
            v-bind="eduData"
            :page-title="pageTitle"
          />
          <div class="edu-menu hidden bg-www-color-accent-blue md:block">
            <div
              :class="{
                'mx-auto max-w-1920px px-5 py-5 lg:px-10': true,
              }"
            >
              <EducationMenu
                v-if="eduData"
                v-bind="eduData"
              />
            </div>
          </div>
        </div>
        <ClientOnly>
          <template v-if="isUnifiedSignupForm">
            <div
              :class="{
                'uss-lp-header container mx-auto': true,
              }"
            >
              <EduHeaderLogo
                v-bind="eduData"
                :class="{
                  'mx-auto mt-60px': true,
                }"
              />
            </div>
            <HekateProgressBar />
            <HekateLoader />
          </template>
        </ClientOnly>
        <UssFormApp
          v-if="isUnifiedSignupForm"
          :id="ussIsStartScreen ? '' : ussFormContainerId"
          :class="{ 'uss-form-container mb-0': true }"
          :style="`--checkImageSrc: url(${checkImageSrc})`"
        />

        <main
          v-if="ussIsStartScreen"
          :id="ussFormContainer"
          ref="page"
          :class="{
            'uss-form-container education-wrapper mb-0': true,
            'standard-min-height': footerData && cleanedFooter === 'standard',
            'lp-min-height': footerData && cleanedFooter === 'lp',
          }"
          :style="`--checkImageSrc: url(${checkImageSrc})`"
        >
          <slot />
        </main>
      </template>
    </template>
    <!-- Case of Enterprise pages with no header -->
    <template v-else-if="cleanedHeader === 'none'">
      <main ref="page">
        <slot />
      </main>
    </template>

    <template v-if="cleanedFooter !== 'none'">
      <Footer
        v-if="footerData && cleanedFooter === 'standard'"
        v-bind="footerData"
      />
      <LpFooter
        v-else-if="footerData && cleanedFooter === 'lp'"
        v-bind="footerData"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { stegaClean } from '@sanity/client/stega';
import type {
  Generic,
  Header as HeaderType,
  Footer as FooterType,
  EducationHeader as EducationHeaderType,
} from '~/sanity/types';
import * as AssertThat from '~/utils/AssertThat';
import { useHashScroll } from '~/composables/useHashScroll';
import UtilityNavbar from '~/components/Organisms/UtilityBar/UtilityNavbar.vue';
import Header from '~/components/Organisms/Header/Header.vue';
import LpHeader from '~/components/Organisms/LpHeader/LpHeader.vue';
import HekateProgressBar from '~/components/Atmos/HekateProgressBar/HekateProgressBar.vue';
import HekateLoader from '~/components/Atmos/HekateLoader/HekateLoader.vue';
import Footer from '~/components/Organisms/Footer/Footer.vue';
import LpFooter from '~/components/Organisms/LpFooter/LpFooter.vue';
import EducationHeaderComponent from '~/components/Organisms/EducationHeader/EduHeader.vue';
import EducationMenu from '~/components/Organisms/EducationHeader/EduMenu.vue';
import EduHeaderLogo from '~/components/Organisms/EducationHeader/EduHeaderLogo.vue';
import UssFormApp from '~/components/Atmos/Form/UssFormApp.vue';
import { useFormState } from '~/composables/useFormState';

const { isUnifiedSignupForm, ussIsStartScreen } = useFormState();
const ussFormContainer = ref('');
const ussFormContainerId = 'uss-form-container';
watchEffect(
  () => {
    ussFormContainer.value = isUnifiedSignupForm.value ? ussFormContainerId : '';
  },
  {
    flush: 'post',
  }
);
interface Props {
  header?: Generic['header'];
  footer?: Generic['footer'];
  pageTitle?: Generic['title'];
}

const { header = 'standardDark', footer = 'standard', pageTitle = '' } = defineProps<Props>();
const cleanedHeader = stegaClean(header).trim();
const cleanedFooter = stegaClean(footer).trim();

let headerType: typeof cleanedHeader | 'educationHeader';
let headerCurrentSlug: typeof cleanedHeader | 'education-header';

if (header === 'standardDark' || header === 'standardWhite') {
  headerType = 'header';
  headerCurrentSlug = 'header';
} else if (header === 'lpDark' || header === 'lpWhite') {
  headerType = 'header';
  headerCurrentSlug = 'landing-page-header';
} else if (header === 'edu') {
  headerType = 'educationHeader';
  headerCurrentSlug = 'education-header';
} else {
  headerType = 'header';
  headerCurrentSlug = 'header';
}

const headerData = ref<HeaderType | null | undefined>(null);
if (
  header === 'standardDark' ||
  header === 'standardWhite' ||
  header === 'lpDark' ||
  header === 'lpWhite'
) {
  const query = groq`*[ _type == $headerType && slug.current == $headerCurrentSlug]`;
  const { data: headerDataList } = await useWwwSanityQueryNonNull<HeaderType[]>(query, {
    headerType,
    headerCurrentSlug,
  });

  headerData.value = AssertThat.getFirstOrThrow(
    headerDataList,
    `No header data found for header type: ${headerType} and slug: ${headerCurrentSlug}`
  );
}

const eduData = ref<EducationHeaderType | null | undefined>(null);
if (header === 'edu') {
  const eduquery = groq`*[ _type == $headerType && slug.current == $headerCurrentSlug]`;
  const { data: eduDataList } = await useWwwSanityQueryNonNull<EducationHeaderType[]>(eduquery, {
    headerType,
    headerCurrentSlug,
  });

  eduData.value = AssertThat.getFirstOrThrow(
    eduDataList,
    `No education header data found for header type: ${headerType} and slug: ${headerCurrentSlug}`
  );
}

AssertThat.someNonNull(eduData.value, headerData.value, cleanedHeader === 'none' ? true : null);

const footerType = footer === 'standard' ? 'footer' : 'lp';
const queryFooter = groq`*[ _type == "footer" && slug.current == $footerType ][0]`;
const { data: footerData } = await useWwwSanityQueryNonNull<FooterType>(queryFooter, {
  footerType,
});

AssertThat.nonNullRef(footerData, `No footer data found for footer type: ${footerType}`);

const isHeaderLight = computed(() => {
  if (header === 'standardWhite' || header === 'lpWhite') {
    return true;
  }
  return false;
});
const { $staticImages } = useNuxtApp();
const checkImageSrc = $staticImages['check'];

const page = ref<HTMLElement | null>(null);
const nuxtApp = useNuxtApp();
nuxtApp.hook('page:finish', () => {
  if (!page.value) return;
  useHashScroll(page.value);
});
</script>

<style>
.uss-lp-header .logo {
  margin-top: 50px !important;
}
.hekate-uss .uss-form-container h2 {
  color: var(--www-color-neutral-medium-dark);
  font-size: 20px !important;
  font-weight: 700 !important;
  line-height: 28.1px !important;
  margin-bottom: 15px !important;
  text-align: center;
}

@media (min-width: 768px) {
  .uss-lp-header img {
    max-width: 170px !important;
    margin-top: 60px !important;
  }
  .hekate-uss .uss-form-container h2 {
    font-size: 24px !important;
  }
}
@media (min-width: 1024px) {
  .uss-lp-header img {
    height: 49px !important;
    min-width: 216px !important;
  }
  .hekate-uss .uss-form-container h2 {
    font-size: 32px !important;
  }
  .standard-min-height {
    min-height: calc(100vh - 551px) !important;
  }
  .lp-min-height {
    min-height: calc(100vh - 100px) !important;
  }
}

@media (max-width: 768px) {
  .uss-lp-header .logo {
    margin-top: 20px !important;
  }
}
</style>
